/**
 * @prettier
 */

import React, { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import cx from 'classnames';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import ZolaHeartsLogo from '@zola/zola-ui/src/assets/images/logos/zola-logo-hearts.svg';
import ConfirmationIcon from 'assets/images/invitations/single-sample-modal/confirmation-icon.svg';
import Interstitial1Icon from 'assets/images/invitations/single-sample-modal/interstitial-1.svg';
import Interstitial2Icon from 'assets/images/invitations/single-sample-modal/interstitial-2.svg';
import Interstitial3Icon from 'assets/images/invitations/single-sample-modal/interstitial-3.svg';
import Layout from '../Layout/Layout';
import SamplePreview from '../SamplePreview/SamplePreview';
import { submitSingleSampleForm } from '../util/api';
import { usePhotoCache } from '../util/hooks';
import { trackEvent } from '../../../../../util/trackingHelper';
import { STEPS } from '../constants/steps';

import styles from './styles.module.less';

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const INTERSTITIAL_DELAY = 2000;
const INTERSTITIAL_LINGER = 2000;
const failureRedirects = [
  { errorKey: 'weddingDetails', stepKey: STEPS.WEDDING_DETAILS_FORM },
  { errorKey: 'shipping', stepKey: STEPS.SHIPPING_DETAILS_FORM },
  { errorKey: 'account', stepKey: STEPS.ACCOUNT_DETAILS_FORM },
];

const Interstitial = ({ show, icon, text }) => {
  return (
    <div className={cx(styles.interstitialCol, { [styles.hidden]: !show })}>
      <div className={styles.interstitialIcon}>
        <img src={icon} alt="" />
      </div>
      <div className={styles.interstitialText}>{text}</div>
    </div>
  );
};

const InterstitialPhase = ({ interstitialStep, setModalOptions }) => {
  useEffect(() => {
    setModalOptions({
      preventClose: true,
      hideClose: true,
      closeCallback: () => {},
    });
  }, []);

  return (
    <div className={styles.interstitialPhaseBox}>
      <div className={styles.processingBox}>
        <img className={styles.processingIcon} src={ZolaHeartsLogo} alt="" />
        <div className={styles.processingText}>Processing your order...</div>
      </div>
      <div className={styles.interstitialBox}>
        <div className={styles.interstitialRow}>
          <Interstitial
            show={interstitialStep >= 1}
            icon={Interstitial1Icon}
            text="30% off when you place a full order"
          />
          <Interstitial
            show={interstitialStep >= 2}
            icon={Interstitial2Icon}
            text="Free guest addressing and envelopes"
          />
          <Interstitial
            show={interstitialStep >= 3}
            icon={Interstitial3Icon}
            text="Matching invitation suites for all designs"
          />
        </div>
      </div>
    </div>
  );
};

const randomInRange = ([min, max]) => Math.random() * (max - min) + min;

const shootFireworks = (numFireworks, maxFireworks = numFireworks) => {
  if (numFireworks <= 0) {
    return;
  }

  const options = {
    particle: { startVelocity: 30, spread: 360, ticks: 60, zIndex: 2050 },
    particleCount: 50,
    ranges: [
      { x: [0.1, 0.3], y: [-0.2, 0.8] },
      { x: [0.7, 0.9], y: [-0.2, 0.8] },
    ],
    delay: 1000,
  };

  setTimeout(() => {
    options.ranges.forEach(range => {
      const origin = { x: randomInRange(range.x), y: randomInRange(range.y) };

      const particleCount = options.particleCount * (numFireworks / maxFireworks);

      confetti({ ...options.particle, particleCount, origin });
    });

    shootFireworks(numFireworks - 1, maxFireworks);
  }, options.delay);
};

const CompletePhase = ({
  completedOrder,
  form,
  handleClose,
  originalModalOptions,
  setModalOptions,
  template,
  sampleNotCustomizable,
  skuData,
  cardSuiteDetails,
  cardSuiteDetailsSet,
  isMagnet,
  isMulti,
  setMultiPreviewState,
  multiPreviewState,
  cardSuiteUUIDList,
}) => {
  useEffect(() => {
    shootFireworks(4);
  }, []);

  useEffect(() => {
    setModalOptions({
      ...originalModalOptions,
      warnOnClose: false,
      preventClose: false,
      hideClose: false,
    });
  }, [originalModalOptions]);

  return (
    <div className={styles.completePhaseBox}>
      <Layout.Col>
        <Layout.Row>
          <Layout.PreviewBox>
            <SamplePreview
              hideColorPicker
              form={form}
              template={template}
              sampleNotCustomizable={sampleNotCustomizable}
              skuData={skuData}
              cardSuiteDetails={cardSuiteDetails}
              isMagnet={isMagnet}
              isMulti={isMulti}
              setMultiPreviewState={setMultiPreviewState}
              multiPreviewState={multiPreviewState}
              cardSuiteUUIDList={cardSuiteUUIDList}
              cardSuiteDetailsSet={cardSuiteDetailsSet}
            />
          </Layout.PreviewBox>
          <div className={styles.confirmationBox}>
            <img className={styles.confirmationIcon} src={ConfirmationIcon} alt="" />
            <Layout.Header>
              <span className={styles.header}>Thank you for your order!</span>
            </Layout.Header>
            <Layout.Text>
              You’ll receive an order confirmation shortly at{' '}
              <span className={styles.bold}>{completedOrder.email}</span>.
            </Layout.Text>
            <Layout.Text>
              Your order number is <span className={styles.bold}>{completedOrder.id}</span>.
            </Layout.Text>
            <div className={styles.closeButtonBox}>
              <ButtonV2 onClick={handleClose}>Continue Shopping</ButtonV2>
            </div>
          </div>
        </Layout.Row>
      </Layout.Col>
    </div>
  );
};

const FlowCompletion = ({
  cardSuiteDetails,
  template,
  isGuest,
  wasGuestOnLoad,
  userContext,
  fetchUserContext,
  handleFlowError,
  fetchSampleLimitExhausted,
  form,
  completedOrder,
  setCompletedOrder,
  trackingParams,
  closeModal,
  setModalOptions,
  originalModalOptions,
  sampleNotCustomizable,
  nonEditableSingleSampleSku,
  skuData,
  isMagnet,
  isMulti,
  multiPreviewState,
  setMultiPreviewState,
  cardSuiteDetailsSet,
  cardSuiteUUIDList,
}) => {
  const [interstitialStep, setInterstitialStep] = useState(0);

  const photoUploadHandler = usePhotoCache();

  const handleClose = useCallback(() => {
    if (wasGuestOnLoad) {
      window.location.href = window.location.pathname;
    } else {
      closeModal().then(() => {
        window.location.href = window.location.pathname;
      });
    }
  }, [wasGuestOnLoad]);

  useEffect(() => {
    const apiResponse = submitSingleSampleForm(
      cardSuiteDetails,
      form,
      isGuest,
      userContext,
      fetchUserContext,
      photoUploadHandler,
      sampleNotCustomizable,
      nonEditableSingleSampleSku,
      isMagnet,
      isMulti,
      cardSuiteUUIDList,
      cardSuiteDetailsSet
    ).then(response => {
      trackEvent('Flow Completed', {
        ...trackingParams,
        is_error: !!response.errors,
      });

      if (response.errors) {
        const failedRedirect = failureRedirects.find(r =>
          Object.keys(response.errors).includes(r.errorKey)
        );

        setModalOptions({ ...originalModalOptions, hideClose: false });

        handleFlowError(
          response.errors,
          failedRedirect ? failedRedirect.stepKey : failureRedirects[0].stepKey
        );
        return null;
      }

      fetchSampleLimitExhausted();
      return response.orderDetails;
    });

    const interstitialPromise = delay(INTERSTITIAL_DELAY)
      .then(() => setInterstitialStep(1))
      .then(() => delay(INTERSTITIAL_DELAY))
      .then(() => setInterstitialStep(2))
      .then(() => delay(INTERSTITIAL_DELAY))
      .then(() => setInterstitialStep(3))
      .then(() => delay(INTERSTITIAL_LINGER));

    Promise.all([apiResponse, interstitialPromise]).then(([orderDetails]) => {
      setCompletedOrder(orderDetails);
    });
  }, []);

  return completedOrder ? (
    <CompletePhase
      {...{
        completedOrder,
        form,
        handleClose,
        originalModalOptions,
        template,
        skuData,
        sampleNotCustomizable,
        setModalOptions,
        cardSuiteDetails,
        isMagnet,
        isMulti,
        multiPreviewState,
        setMultiPreviewState,
        cardSuiteDetailsSet,
        cardSuiteUUIDList,
      }}
    />
  ) : (
    <InterstitialPhase interstitialStep={interstitialStep} setModalOptions={setModalOptions} />
  );
};

export default FlowCompletion;
